import { useMutation } from "@tanstack/react-query";
import {
  DeployParams,
  Modes,
  UpdateCurrentUserParams,
  getEnumName,
} from "@theprelude/sdk";
import { signInWithRedirect, confirmSignIn } from "../lib/cognito";
import {
  service,
  serviceURL,
  useAuthConfig,
  useAuthStore,
} from "../stores/auth-store";

export const useRegisterUser = () => {
  return useMutation({
    mutationFn: async (params: {
      email: string;
      name: string;
      company: string;
    }) => {
      return service({
        host: serviceURL,
      }).iam.newAccountUser({
        email: params.email,
        name: params.name,
        company: params.company,
      });
    },
  });
};

export const useConfirmSignIn = () => {
  return useMutation({
    mutationFn: (data: { newPassword: string }) =>
      confirmSignIn({
        challengeResponse: data.newPassword,
      }),
  });
};

export const useSignIn = () => {
  const signIn = useAuthStore((state) => state.signIn);
  return useMutation({
    mutationFn: (data: { email: string; password: string }) =>
      signIn({
        email: data.email,
        password: data.password,
      }),
  });
};

type SocialProviders = "Google";

export const useSocialSignIn = () => {
  return useMutation({
    mutationFn: (data: { provider: SocialProviders; handle?: string }) => {
      if (data.provider === "Google") {
        return signInWithRedirect({
          provider: "Google",
          options: {
            loginHint: data.handle,
          },
        });
      }

      throw new Error("Invalid provider");
    },
  });
};

export const useSSOLogin = () => {
  return useMutation({
    mutationFn: async (data: { slug: string; handle?: string }) => {
      const { oidc } = await service({ host: serviceURL }).iam.getOIDC(
        data.slug,
      );

      if (!oidc) {
        throw new Error("OIDC is not enabled for this account");
      }

      return signInWithRedirect({
        provider: {
          custom: oidc,
        },
        options: {
          loginHint: data.handle,
        },
      });
    },
  });
};

export const useResetPassword = () => {
  return useMutation({
    mutationFn: (data: { email: string }) =>
      service({ host: serviceURL }).iam.forgotPassword({
        handle: data.email,
      }),
  });
};

export const useConfirmResetPassword = () => {
  return useMutation({
    mutationFn: (data: { email: string; code: string; password: string }) =>
      service({ host: serviceURL }).iam.forgotPassword({
        handle: data.email,
        confirmation_code: data.code,
        password: data.password,
      }),
  });
};

export const usePartnerEndpointDeploy = () => {
  const authConfig = useAuthConfig();

  return useMutation({
    mutationFn: (params: DeployParams) =>
      service(authConfig).partner.deploy(params),
  });
};

export const useResumeTests = () => {
  const authConfig = useAuthConfig();
  return useMutation({
    mutationFn: async () => {
      await service(authConfig).iam.updateAccount({
        mode: getEnumName(Modes, Modes.MANUAL),
      });
    },
  });
};

export const useCompile = () => {
  const authConfig = useAuthConfig();
  return useMutation({
    mutationFn: ({
      code,
      filename,
      source_test_id,
    }: {
      code: string;
      filename?: string;
      source_test_id?: string;
    }) => service(authConfig).build.compile(code, filename, source_test_id),
  });
};

export const useGenerateTechnique = () => {
  const authConfig = useAuthConfig();
  return useMutation({
    mutationFn: async ({
      technique,
      parent_job_id,
      intel_context,
    }: {
      technique: string;
      parent_job_id?: string;
      intel_context?: string;
    }) => {
      return service(authConfig).generate.generateTechnique(
        technique,
        parent_job_id,
        intel_context,
      );
    },
  });
};

export const useChangePassword = () => {
  const authConfig = useAuthConfig();
  return useMutation({
    mutationFn: (data: { oldPassword: string; newPassword: string }) =>
      service(authConfig).iam.changePassword({
        current_password: data.oldPassword,
        new_password: data.newPassword,
      }),
  });
};

export const useUpdateCurrentUser = () => {
  const authConfig = useAuthConfig();
  return useMutation({
    mutationFn: (data: UpdateCurrentUserParams) =>
      service(authConfig).iam.updateCurrentUser(data),
  });
};
